/**
 * Open a mask from global search — simple tab open, detail bootstrap, or parent fallback.
 */

import { getRequest } from '../../api/ApiController';
import { createMenuTabAction } from './menuSearch';
import {
  getMaskEntry,
  isMaskEntryVisible,
  normalizeMaskCode,
} from './maskRegistry';

function showToast(toast, severity, summary, detail) {
  toast?.current?.show?.({
    severity,
    summary,
    detail,
    life: 3500,
  });
}

/**
 * Extract ordered rows from a canonical parent-list response.
 */
export function extractListRows(response, style = 'array') {
  if (style === 'paged') {
    const data = response?.data?.data ?? response?.data ?? [];
    return Array.isArray(data) ? data : [];
  }
  const data = response?.data ?? response;
  return Array.isArray(data) ? data : [];
}

/**
 * Fetch page-1 / full canonical parent list for a detail entry.
 * Does not mount parent UI. One request only.
 */
export async function fetchParentListRows(entry, { getRequestFn = getRequest } = {}) {
  if (!entry?.listEndpoint) {
    throw new Error('parent_unavailable');
  }
  const style = entry.listResponseStyle || 'array';
  let url = entry.listEndpoint;
  if (style === 'paged' && entry.listQuery) {
    const params = new URLSearchParams();
    Object.entries(entry.listQuery).forEach(([k, v]) => {
      if (v != null && v !== '') params.set(k, String(v));
    });
    const qs = params.toString();
    url = qs ? `${entry.listEndpoint}${entry.listEndpoint.includes('?') ? '&' : '?'}${qs}` : entry.listEndpoint;
  }
  const response = await getRequestFn(url);
  return extractListRows(response, style);
}

export function buildDetailProps(entry, entity) {
  const strategy = entry.propStrategy || 'entity';
  const openType = entry.openType || 'edit';
  const base = { type: openType, not_last_used: true };

  if (entry.navigationStyle === 'navigation_context') {
    base.navigationContext = { filters: [], sorters: [] };
  }

  if (strategy === 'customer') {
    return {
      ...base,
      customerId: entity?.id,
      customerRow: entity,
    };
  }
  if (strategy === 'companyRow') {
    return {
      ...base,
      companyRow: entity,
      ...(entry.navigationStyle === 'navigation_context'
        ? { navigationContext: { filters: [], sorters: [] } }
        : {}),
    };
  }
  if (strategy === 'ticketId') {
    return { ticketId: entity?.id, ...base };
  }
  if (strategy === 'checklist') {
    return {
      ...base,
      checklist_record: entity,
      recordId: entity?.id,
      navigationContext: { filters: [], sorters: [] },
    };
  }
  return {
    ...base,
    [entry.entityProp]: entity,
  };
}

function buildDetailTabAction(entry, entity, t) {
  const tr = typeof t === 'function' ? t : (k) => k;
  const tabKey = entry.tabTranslationKey || entry.translationKey || entry.page;
  const name = tr(tabKey);
  return {
    type: 'create_new_tab',
    name,
    legacyLabel: name,
    translationKey: tabKey,
    page: entry.page,
    mask: entry.maskCode,
    props: buildDetailProps(entry, entity),
  };
}

function buildSimpleTabAction(entry, t) {
  const syntheticItem = {
    page: entry.page,
    title: entry.translationKey,
    tabName: entry.translationKey,
    mask: entry.maskCode,
    props: entry.props || {},
  };
  const action = createMenuTabAction(syntheticItem, entry.parentTitle, t);
  if (action) return action;
  const tr = typeof t === 'function' ? t : (k) => k;
  const name = tr(entry.translationKey || entry.page);
  return {
    type: 'create_new_tab',
    name,
    legacyLabel: name,
    translationKey: entry.translationKey || undefined,
    page: entry.page,
    mask: entry.maskCode,
    props: entry.props || {},
  };
}

async function openParentFallback({
  entry,
  userPermissions,
  dispatch,
  t,
  toast,
  setVisible,
  setDialogText,
  getRequestFn,
}) {
  const parent = getMaskEntry(entry.parentMask);
  showToast(
    toast,
    'info',
    t?.('Common.Info') || 'Info',
    t?.(entry.reasonKey || 'MaskSearch.RequiresContext') || 'This screen requires business context',
  );
  if (!parent) {
    return { ok: false, reason: 'parent_unavailable' };
  }
  return openMaskFromSearch({
    entry: parent,
    userPermissions,
    dispatch,
    t,
    toast,
    setVisible,
    setDialogText,
    getRequestFn,
  }).then((result) => ({
    ...result,
    reason: result.ok ? 'parent_fallback' : result.reason,
    fallbackFrom: entry.maskCode,
  }));
}

/**
 * Open mask by registry entry (or mask code string).
 *
 * @returns {Promise<{ ok: boolean, reason?: string, openedId?: *, action?: object }>}
 */
export async function openMaskFromSearch({
  maskCode,
  entry: entryArg,
  userPermissions,
  dispatch,
  t,
  toast,
  setVisible,
  setDialogText,
  getRequestFn = getRequest,
}) {
  const code = normalizeMaskCode(maskCode || entryArg?.maskCode);
  const entry = entryArg || getMaskEntry(code);

  if (!entry) {
    showToast(toast, 'warn', t?.('Common.Error') || 'Error', t?.('MaskSearch.UnknownMask') || 'Unknown mask');
    return { ok: false, reason: 'unknown' };
  }

  if (!isMaskEntryVisible(entry, userPermissions)) {
    showToast(toast, 'warn', t?.('Common.Error') || 'Error', t?.('MaskSearch.Unauthorized') || 'Unauthorized');
    return { ok: false, reason: 'unauthorized' };
  }

  if (entry.openStrategy === 'parent_fallback') {
    return openParentFallback({
      entry,
      userPermissions,
      dispatch,
      t,
      toast,
      setVisible,
      setDialogText,
      getRequestFn,
    });
  }

  if (!entry.requiredContext) {
    const action = buildSimpleTabAction(entry, t);
    dispatch(action);
    return { ok: true, action, reason: 'simple' };
  }

  // DETAIL / FORM_WITH_CONTEXT bootstrap via canonical parent list
  if (!entry.parentMask || !entry.listEndpoint || !entry.entityProp) {
    showToast(toast, 'warn', t?.('Common.Error') || 'Error', t?.('MaskSearch.ParentUnavailable') || 'Parent unavailable');
    return { ok: false, reason: 'parent_unavailable' };
  }

  try {
    setDialogText?.(t?.('Util.Loading.Detail') || 'Loading');
    setVisible?.(true);
    const rows = await fetchParentListRows(entry, { getRequestFn });
    if (!rows.length) {
      showToast(
        toast,
        'info',
        t?.('Common.Info') || 'Info',
        t?.('MaskSearch.NoRecords') || t?.('Common.NoData') || 'No records available',
      );
      return { ok: false, reason: 'empty' };
    }

    const idField = entry.idField || 'id';
    const first = rows[0];
    const openedId = first?.[idField];
    if (openedId == null) {
      showToast(toast, 'warn', t?.('Common.Error') || 'Error', t?.('MaskSearch.MissingId') || 'Missing id');
      return { ok: false, reason: 'missing_id' };
    }

    if (entry.loadReduxAction?.type && entry.loadReduxAction?.payloadKey) {
      dispatch({
        type: entry.loadReduxAction.type,
        [entry.loadReduxAction.payloadKey]: rows,
      });
    }

    const action = buildDetailTabAction(entry, first, t);
    dispatch(action);
    return { ok: true, action, openedId, reason: 'detail', rows };
  } catch (err) {
    showToast(
      toast,
      'error',
      t?.('Common.Error') || 'Error',
      t?.('MaskSearch.ParentLoadFailed') || t?.('Common.LoadFailed') || 'Load failed',
    );
    return { ok: false, reason: 'parent_load_failed', error: err };
  } finally {
    setVisible?.(false);
  }
}

/**
 * Resolve open target from a menu leaf (synthetic or normal).
 */
export async function openMenuSearchItem({
  item,
  parentTitle,
  userPermissions,
  dispatch,
  t,
  toast,
  setVisible,
  setDialogText,
  getRequestFn = getRequest,
}) {
  if (!item) return { ok: false, reason: 'unknown' };

  const registryEntry = item._registryEntry || (item.mask ? getMaskEntry(item.mask) : null);

  if (registryEntry?.requiredContext || registryEntry?.openStrategy === 'parent_fallback') {
    return openMaskFromSearch({
      entry: registryEntry,
      userPermissions,
      dispatch,
      t,
      toast,
      setVisible,
      setDialogText,
      getRequestFn,
    });
  }

  const action = createMenuTabAction(item, parentTitle, t);
  if (!action) return { ok: false, reason: 'no_action' };
  dispatch(action);
  return { ok: true, action, reason: 'simple' };
}
